/**
 * Turns on Klaviyo's back-in-stock popup with live's configuration.
 *
 * Live calls `klaviyo.init` / `klaviyo.enable('backinstock', …)` inline, right
 * after a blocking onsite.js. Here onsite.js is deferred so it does not hold up
 * the buy box, which means its API can land after this module runs — hence the
 * short retry window instead of assuming script order.
 */
const API_RETRY_INTERVAL = 250;
const API_MAX_RETRIES = 20;

export class KlaviyoBackInStock extends HTMLElement {
  /** @type {number} */
  retryTimer = 0;

  /** @type {number} */
  retries = 0;

  connectedCallback() {
    this.enableBackInStock();
  }

  disconnectedCallback() {
    clearTimeout(this.retryTimer);
  }

  /**
   * Trigger and modal options rendered by component-klaviyo-back-in-stock.liquid.
   * @returns {object | null}
   */
  get config() {
    const json = this.querySelector('script[data-klaviyo-bis-config]')?.textContent;
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch (error) {
      console.error('Klaviyo back-in-stock config is not valid JSON', error);
      return null;
    }
  }

  /**
   * Registers the back-in-stock form, retrying while onsite.js loads.
   * @returns {void}
   */
  enableBackInStock() {
    const { klaviyo } = window;

    if (typeof klaviyo?.init !== 'function' || typeof klaviyo?.enable !== 'function') {
      if (this.retries >= API_MAX_RETRIES) return;

      this.retries += 1;
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.enableBackInStock(), API_RETRY_INTERVAL);
      return;
    }

    // A sibling color swap can mount a second instance; Klaviyo only wants one.
    if (window.klaviyoBackInStockEnabled) return;

    const config = this.config;
    if (!config) return;

    window.klaviyoBackInStockEnabled = true;
    klaviyo.init({ account: this.dataset.account, platform: 'shopify' });
    klaviyo.enable('backinstock', config);
  }
}

if (!customElements.get('klaviyo-back-in-stock')) {
  customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
}
